import { TablePrimitiveHeader, TablePrimitiveRow, TablePrimitiveHead } from '@components/organisms';
import { flexRender, type HeaderGroup } from '@tanstack/react-table';
import { cn } from '@utils';
import * as React from 'react';
import type { DataGridBaseRow } from '../../data-grid.type';
import { resolveDataGridHeaderWidth } from '../body/cell-width';
import { DataGridHeaderMenu } from './menu';

interface DataGridHeaderProps<T extends DataGridBaseRow> {
	headerGroups: HeaderGroup<T>[];
	headerMenu?: DataGridHeaderMenuProps; // This has been deprecated due too the column visibility menu
	handleHideColumn: (columnKey: string) => void;
	handleSortAsc: (columnKey: string) => void;
	handleSortDesc: (columnKey: string) => void;
	resizingColumnId: string; // The column that is resizing
}

export interface DataGridHeaderMenuProps {
	columnKey: string;
	hideColumn?: (columnKey: string) => void;
	sortAsc?: (columnKey: string) => void;
	sortDesc?: (columnKey: string) => void;
}

const dontAllowHeaderMenu = ['actions'];

/**
 * A flexible header component for data grids that supports column resizing, sorting,
 * and customizable header menus.
 *
 * @component
 * @template TData - The type of data being displayed in the grid rows
 * @template CD - Optional custom data type for column definitions
 *
 * @property {HeaderGroup<TData>[]} headerGroups - Array of header groups from @tanstack/react-table
 *   Defines the structure and hierarchy of the table headers
 *   Each group can contain multiple headers for complex table layouts
 *
 * @property {DataGridHeaderMenuProps} [headerMenu] - Configuration for the header menu
 *   Note: This property has been deprecated in favor of the column visibility menu
 *   Controls the menu that appears in column headers for sorting and visibility options
 *
 * @property {(columnKey: string) => void} handleHideColumn - Callback to hide a column
 *   Called when a user chooses to hide a column from the header menu
 *   - columnKey: Unique identifier for the column to hide
 *
 * @property {(columnKey: string) => void} handleSortAsc - Callback to sort a column ascending
 *   Called when a user chooses to sort a column in ascending order
 *   - columnKey: Unique identifier for the column to sort
 *
 * @property {(columnKey: string) => void} handleSortDesc - Callback to sort a column descending
 *   Called when a user chooses to sort a column in descending order
 *   - columnKey: Unique identifier for the column to sort
 *
 * @property {string} resizingColumnId - ID of the column currently being resized
 *   Used to highlight the resize handle of the active column
 *   Empty string when no column is being resized
 *
 * @example
 * // Basic usage with header groups
 * <DataGridHeader
 *   headerGroups={tableHeaderGroups}
 *   handleHideColumn={(key) => console.log(`Hide column ${key}`)}
 *   handleSortAsc={(key) => console.log(`Sort ${key} ascending`)}
 *   handleSortDesc={(key) => console.log(`Sort ${key} descending`)}
 *   resizingColumnId=""
 * />
 *
 * @example
 * // With custom header menu and row selection
 * <DataGridHeader
 *   headerGroups={tableHeaderGroups}
 *   headerMenu={{
 *     columnKey: 'name',
 *     hideColumn: handleHideColumn,
 *     sortAsc: handleSortAsc,
 *     sortDesc: handleSortDesc
 *   }}
 *   handleHideColumn={handleHideColumn}
 *   handleSortAsc={handleSortAsc}
 *   handleSortDesc={handleSortDesc}
 *   resizingColumnId="name"
 * />
 *
 * @example
 * // With custom styling for resize handles
 * <DataGridHeader
 *   headerGroups={tableHeaderGroups}
 *   handleHideColumn={handleHideColumn}
 *   handleSortAsc={handleSortAsc}
 *   handleSortDesc={handleSortDesc}
 *   resizingColumnId={resizingColumn}
 *   className="custom-header"
 *   style={{
 *     // Custom styles for header cells
 *     '& .resize-handle': {
 *       backgroundColor: 'blue',
 *       opacity: 0.5
 *     }
 *   }}
 * />
 */
export function DataGridHeader<T extends DataGridBaseRow>({
	headerGroups,
	headerMenu,
	handleHideColumn,
	handleSortAsc,
	handleSortDesc,
	resizingColumnId,
}: DataGridHeaderProps<T>) {
	return (
		<TablePrimitiveHeader>
			{headerGroups.map(headerGroup => {
				const headers = headerGroup.headers;
				const lastHeaderIndex = headers.length - 1;
				const hasActionsColumn = lastHeaderIndex >= 0 && headers[lastHeaderIndex].id === 'actions';

				return (
					<TablePrimitiveRow key={headerGroup.id} data-aid="data-grid-header-row">
						{headerGroup.headers.map((header, i) => {
							const isActionsColumn = header.id === 'actions';
							const isLastColumn = i === lastHeaderIndex;
							const shouldAllowHeaderMenu = !dontAllowHeaderMenu.includes(header.id);
							const headerContext = header.getContext();

							const isSelectionColumn = header.column.id === 'selection';
							const fixedColumnStyle = resolveDataGridHeaderWidth({
								isSelectionColumn,
								fallbackWidth: header.getSize(),
							});

							return (
								<TablePrimitiveHead
									key={header.id}
									style={{
										...fixedColumnStyle,
										...(isActionsColumn ? { textAlign: 'right' } : {}),
									}}
									className={cn(
										'relative', // Required for absolute positioning of resize handle
										header.column.getCanResize() && 'select-none', // Makes the resizing look better
										isActionsColumn && 'w-auto', // Add classes to push actions column to the right
										isActionsColumn && hasActionsColumn && isLastColumn && 'ml-auto text-right' // Make actions column hug the right edge
									)}
									data-aid={`data-grid-header-cell-${header.id}`}
								>
									{header.isPlaceholder ? null : (
										<div
											className={cn(
												'flex items-center',
												// Justify the actions column content to the right
												isActionsColumn ? 'justify-end' : 'justify-between'
											)}
										>
											{flexRender(header.column.columnDef.header, headerContext)}
											{header.column.getCanResize() && (
												// eslint-disable-next-line jsx-a11y/no-static-element-interactions
												<div
													onMouseDown={header.getResizeHandler()}
													onTouchStart={header.getResizeHandler()}
													className={cn(
														'absolute right-0 top-0 h-full w-1 cursor-col-resize touch-none select-none',
														'opacity-100',
														'bg-gray-200 hover:bg-gray-400',
														resizingColumnId === header.id && 'bg-gray-400 opacity-100' // Show handle while this column is resizing
													)}
												/>
											)}
											{headerMenu && shouldAllowHeaderMenu && (
												<DataGridHeaderMenu
													columnKey={header.id}
													hideColumn={handleHideColumn}
													sortAsc={handleSortAsc}
													sortDesc={handleSortDesc}
												/>
											)}
										</div>
									)}
								</TablePrimitiveHead>
							);
						})}
					</TablePrimitiveRow>
				);
			})}
		</TablePrimitiveHeader>
	);
}
